/**
 * Checks if the browser supports the Cache API.
 *
 * @returns {boolean} `true` if the Cache API is supported, `false` otherwise.
 */
export function isCacheSupported() {
  return 'caches' in window;
}

/**
 * Retrieves data from the cache.
 *
 * @param {string} cacheName - The name of the cache.
 * @param {string} cacheKey - The cacheKey of the cached data.
 * @returns {Promise<object>} The cached data as a JSON object.
 */
export async function getCachedData(cacheName, cacheKey) {
  if (!isCacheSupported()) return null;

  const cacheStorage = await caches.open(cacheName);
  const cachedResponse = await cacheStorage.match(cacheKey);

  if (cachedResponse?.ok) {
    return cachedResponse;
  }

  return null;
}


/**
 * Stores data in the specified cache under the given key.
 *
 * @param {string} cacheName - The name of the cache to store the data in.
 * @param {string} cacheKey - The key to use for storing the data in the cache.
 * @param {any} data - The data to store in the cache.
 * @returns {Promise<void>} A Promise that resolves when the data has been stored in the cache.
 */
export async function setCacheData(cacheName, cacheKey, data) {
  if (!isCacheSupported()) return null;

  const cacheStorage = await caches.open(cacheName);
  try {
    await cacheStorage.put(cacheKey, new Response(data));
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      await deleteOldCaches();
      await cacheStorage.put(cacheKey, new Response(data));
    } else {
      throw error;
    }
  }
};

/**
 * Deletes any old caches to respect user's disk space.
 *
 * @param {string} cachePrefix - The prefix of the cache keys to delete.
 * @param {string} currentCache - The key of the current cache, which should not be deleted.
 */
export async function deleteOldCaches(cachePrefix, currentCache) {
  if (!isCacheSupported()) return null;

  const keys = await caches.keys();
  const oldCaches = keys.filter(key => !cachePrefix || cachePrefix && key.startsWith(cachePrefix) && key !== currentCache);
  oldCaches.forEach(async key => {
    await caches.delete(key);
  });
}